import { useCallback, useImperativeHandle, useMemo, useRef } from "react";
import type { Ref } from "react";
import { ChartOfAccountsTemplate } from "@common/types/chartOfAccountsTemplate";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import {
    ChartOfAccountsTemplateAccountsData,
    useSaveChartOfAccountsTemplateAccountsMutation,
} from "@api/coaTemplate.api";
import {
    ChartOfAccountsTemplateEditorBodyHandle,
    ChartOfAccountsTemplateEditorHandle,
} from "./editorBodyTypes";
import { useTemplateEditorDraft } from "./useTemplateEditorDraft";
import { useEditorDraftValidation } from "./useEditorDraftValidation";
import {
    buildAccountNameKeySnapshot,
    findIntroducedDuplicateNameIds,
    resolveSaveBlockedReason,
} from "./resolveSaveBlockedReason";
import {
    buildTemplateIdentityMap,
    mapServerToDraftAccounts,
    mapServerToDraftGroups,
} from "./templatesHost/templateDraftMapping";
import { makeTemplateRowCapabilities } from "./templatesHost/templateRowCapabilities";
import { useTemplateRoleTools } from "./templatesHost/useTemplateRoleTools";
import { useTemplateImportTools } from "./templatesHost/useTemplateImportTools";
import { toTemplateBulkSavePayload } from "./templatesHost/templateBulkSave";
import {
    workspaceAllocateCodeText,
    workspaceEditorCodeRules,
} from "./workspaceHost/workspaceEditorHost";
import { ChartOfAccountsTemplateNestedEditor } from "./nested/ChartOfAccountsTemplateNestedEditor";

export type { ChartOfAccountsTemplateEditorHandle } from "./editorBodyTypes";

const SAVE_HOTKEY_DESCRIPTION = "Save template accounts";

interface Props {
    template: ChartOfAccountsTemplate;
    serverData: ChartOfAccountsTemplateAccountsData;
    onRequestClose?: () => void;
}

// The templates host: owns the draft, its persistence (the bulk-save mutation),
// per-row capabilities, code rules, and the drawer's imperative handle. It maps
// server rows into the host-agnostic draft, keeps a side map for the off-tree
// template identity, and mounts the nested body with everything it renders.
export const ChartOfAccountsTemplateEditorContent = ({
    template,
    serverData,
    onRequestClose,
    ref,
}: Props & { ref?: Ref<ChartOfAccountsTemplateEditorHandle> }) => {
    const { toast: successToast, error: errorToast } = useToaster();

    const saveMutation =
        useSaveChartOfAccountsTemplateAccountsMutation(template);
    const bodyRef = useRef<ChartOfAccountsTemplateEditorBodyHandle>(null);

    // Side map (id -> off-tree template identity), re-derived from every server
    // payload the host maps. It never stores numeric code — allocation writes a
    // new codeText onto the draft, so a stale number here would serialize wrong.
    const identityMap = useMemo(
        () => buildTemplateIdentityMap(serverData),
        [serverData],
    );

    const initialAccounts = useMemo(
        () => mapServerToDraftAccounts(serverData),
        [serverData],
    );
    const initialGroups = useMemo(
        () => mapServerToDraftGroups(serverData),
        [serverData],
    );

    const draft = useTemplateEditorDraft({
        accounts: initialAccounts,
        groups: initialGroups,
        allocateCodeText: workspaceAllocateCodeText,
    });

    const getRowCapabilities = useMemo(
        () => makeTemplateRowCapabilities({ identityMap }),
        [identityMap],
    );

    const roleTools = useTemplateRoleTools({
        accounts: draft.accounts,
        savedAccounts: serverData.accounts,
        entityLegalType: template.entityLegalType,
        applyRoleAssignments: draft.applyRoleAssignments,
    });

    const importTools = useTemplateImportTools({ draft });

    const validation = useEditorDraftValidation({
        accounts: draft.accounts,
        groups: draft.groups,
        codeRules: workspaceEditorCodeRules,
        getRowCapabilities,
    });

    const seedNameKeyById = useMemo(
        () => buildAccountNameKeySnapshot(initialAccounts),
        [initialAccounts],
    );
    const introducedDuplicateNameIds = useMemo(
        () =>
            findIntroducedDuplicateNameIds({
                accounts: draft.accounts,
                seedNameKeyById,
            }),
        [draft.accounts, seedNameKeyById],
    );

    const saveBlockedReason = resolveSaveBlockedReason({
        hasBlankNames: validation.invalidNameRowIds.size > 0,
        hasDuplicateCodes: validation.duplicateCodeIds.size > 0,
        hasInvalidGroupNames:
            validation.invalidGroupNameIds.size > 0 ||
            validation.duplicateGroupNameIds.size > 0,
        hasIntroducedDuplicateNames: introducedDuplicateNameIds.size > 0,
    });
    const isSaveDisabled = saveBlockedReason !== undefined;

    const { captureSignature, resetToServerIfUnchanged } = draft;

    const handleSave = useCallback(async (): Promise<boolean> => {
        const snapshotSignature = captureSignature();
        try {
            const saved = await saveMutation.mutateAsync(
                toTemplateBulkSavePayload({
                    state: { accounts: draft.accounts, groups: draft.groups },
                    identityMap,
                }),
            );
            resetToServerIfUnchanged({
                nextState: {
                    accounts: mapServerToDraftAccounts(saved),
                    groups: mapServerToDraftGroups(saved),
                },
                snapshotSignature,
            });
            bodyRef.current?.clearSelection();
            successToast("Saved template accounts");
            return true;
        } catch {
            errorToast("Failed to save template accounts");
            return false;
        }
    }, [
        captureSignature,
        draft.accounts,
        draft.groups,
        errorToast,
        identityMap,
        resetToServerIfUnchanged,
        saveMutation,
        successToast,
    ]);

    const handleSaveAndClose = useCallback(async () => {
        const didSave = await handleSave();
        if (didSave) {
            onRequestClose?.();
        }
    }, [handleSave, onRequestClose]);

    useImperativeHandle(
        ref,
        () => ({
            getIsDirty: () => draft.isDirty,
            getSaveBlockedReason: () => saveBlockedReason,
            save: handleSave,
        }),
        [draft.isDirty, saveBlockedReason, handleSave],
    );

    const bodyProps = {
        draft,
        getRowCapabilities,
        validation: {
            invalidNameRowIds: validation.invalidNameRowIds,
            invalidGroupNameIds: validation.invalidGroupNameIds,
            duplicateGroupNameIds: validation.duplicateGroupNameIds,
            duplicateNameIds: validation.duplicateNameIds,
            duplicateCodeIds: validation.duplicateCodeIds,
        },
        // The role drawer shares mod+enter with Save in the main hotkey scope.
        isSaveDisabled: isSaveDisabled || roleTools.isManageOpen,
        saveBlockedReason,
        isSaving: saveMutation.isPending,
        saveHotkeyDescription: SAVE_HOTKEY_DESCRIPTION,
        onSave: handleSave,
        onSaveAndClose: handleSaveAndClose,
    };

    return (
        <>
            <ChartOfAccountsTemplateNestedEditor
                ref={bodyRef}
                {...bodyProps}
                toolbarActionsAddon={
                    <>
                        {roleTools.toolbarButton}
                        {importTools.toolbarButton}
                    </>
                }
                onReassignRole={roleTools.onReassignRole}
            />
            {roleTools.overlays}
            {importTools.overlays}
        </>
    );
};
